import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Lightbulb,
  Users,
  TrendingUp,
  Rocket,
  MessageCircle,
  PhoneCall,
  CalendarDays,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'We push the boundaries of AI to help businesses respond faster and smarter.',
  },
  {
    icon: Users,
    title: 'Customer First',
    description: "Every feature we build starts with understanding our customers' real needs.",
  },
  {
    icon: TrendingUp,
    title: 'Growth Mindset',
    description: 'We believe in continuous improvement for ourselves and our customers.',
  },
  {
    icon: Rocket,
    title: 'Speed & Quality',
    description: 'We move fast without compromising on the quality our customers deserve.',
  },
];

const capabilities = [
  {
    icon: MessageCircle,
    title: 'Answers every channel',
    description: 'Instagram, WhatsApp, Telegram, web chat, and SMS — one AI handles them all.',
  },
  {
    icon: PhoneCall,
    title: 'Picks up the phone',
    description: 'An AI receptionist answers calls, qualifies callers, and takes reservations.',
  },
  {
    icon: CalendarDays,
    title: 'Keeps the calendar organized',
    description: 'Bookings land on your calendar with every detail logged automatically.',
  },
  {
    icon: RefreshCw,
    title: 'Follows up until it closes',
    description: 'Polite, persistent nudges that keep every lead warm and moving.',
  },
  {
    icon: ShieldCheck,
    title: 'Keeps the owner in control',
    description: 'You decide how the AI behaves — and can review what it sends.',
  },
];

const stats = [
  { number: '6+', label: 'Industries Served' },
  { number: '4', label: 'Platforms Supported' },
  { number: '24/7', label: 'AI-Assisted Coverage' },
  { number: '<30s', label: 'Response Time' },
];

const AboutPage: React.FC = () => {
  const [contactOpen, setContactOpen] = React.useState(false);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="overline" component="p" sx={{ mb: 2 }}>
              About CleoLead
            </Typography>
            <Typography variant="h2" component="h1" gutterBottom>
              Building the AI Business Manager
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.125rem' }}
            >
              We're on a mission to give every business one AI that runs the front office —
              answering the phone, replying on every channel, organizing the calendar, closing
              sales loops — with the owner always in control.
            </Typography>
          </Reveal>
        </Container>
      </Box>

      {/* Story Section */}
      <Box
        sx={{
          py: 10,
          bgcolor: 'background.paper',
          borderTop: `1px solid ${tokens.border}`,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal>
                <Typography variant="overline" component="p" sx={{ mb: 2 }}>
                  Our Story
                </Typography>
                <Typography variant="h2" component="h2" sx={{ fontSize: '2rem' }} gutterBottom>
                  Why CleoLead exists
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  CleoLead was born from a simple observation: businesses were losing customers
                  because they couldn't respond fast enough. Traditional chatbots felt robotic and
                  frustrating. We knew AI could do better.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  So we built one AI that works the way a great front-office employee does — it
                  knows the business, answers instantly on whatever channel the customer prefers,
                  keeps the calendar tidy, and never lets a lead go cold.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Today, CleoLead helps businesses across multiple industries manage customer
                  engagement, from local travel agencies to professional services. We're growing
                  every day and we're just getting started.
                </Typography>
              </Reveal>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal delay={tokens.motion.stagger}>
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    borderRadius: `${tokens.radiusCard}px`,
                    p: 4,
                    border: `1px solid ${tokens.border}`,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    sx={{ mb: 1 }}
                  >
                    What CleoLead Does
                  </Typography>
                  {capabilities.map((capability, index) => {
                    const Icon = capability.icon;
                    return (
                      <Box
                        key={capability.title}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          py: 2,
                          borderTop: index > 0 ? `1px solid ${tokens.border}` : 'none',
                        }}
                      >
                        <Box sx={{ color: tokens.copper, mt: 0.25, flexShrink: 0 }}>
                          <Icon size={20} strokeWidth={1.5} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            {capability.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {capability.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" component="p" sx={{ mb: 2 }}>
              Our Values
            </Typography>
            <Typography variant="h2" component="h2" sx={{ fontSize: '2rem' }} gutterBottom>
              The principles that guide everything we do
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={value.title}>
                  <Reveal delay={index * tokens.motion.stagger} sx={{ height: '100%' }}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                        '&:hover': {
                          boxShadow: tokens.shadowLift,
                          borderColor: '#D9CDBC',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: `${tokens.radiusControl}px`,
                            bgcolor: tokens.copperTint,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: tokens.copper,
                            mb: 2.5,
                          }}
                        >
                          <Icon size={22} strokeWidth={1.5} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
                          {value.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {value.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Reveal>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Stats Band */}
      <Box
        sx={{
          py: 7,
          bgcolor: tokens.bandAlt,
          borderTop: `1px solid ${tokens.border}`,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
                <Reveal
                  delay={index * tokens.motion.stagger}
                  sx={{
                    borderLeft: { xs: 'none', md: index > 0 ? '1px solid #D9D0BF' : 'none' },
                    pl: { xs: 0, md: index > 0 ? 3 : 0 },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: tokens.fontDisplay,
                      fontWeight: 700,
                      fontSize: { xs: '2rem', md: '2.6rem' },
                      lineHeight: 1.05,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: tokens.copper,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact CTA */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="sm">
          <Reveal sx={{ textAlign: 'center' }}>
            <Typography variant="overline" component="p" sx={{ mb: 2 }}>
              Get in Touch
            </Typography>
            <Typography variant="h2" component="h2" sx={{ fontSize: '2rem' }} gutterBottom>
              Want to know more?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Whether you're curious about the product, partnerships, or anything else — we'd
              love to hear from you.
            </Typography>
            <Button variant="contained" size="large" onClick={() => setContactOpen(true)}>
              Contact Us
            </Button>
          </Reveal>
        </Container>
      </Box>

      <ContactFormDialog open={contactOpen} onClose={() => setContactOpen(false)} />

      <Footer />
    </Box>
  );
};

export default AboutPage;
